import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { PageNotes, SortInfo, TableHeader, CommonConstants, ColumnDef } from '../../global/interfaces';
import { Chart } from '../../shared/charts/charts.class';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { select, Store } from '@ngrx/store';
import { AppState } from '../../reducers';
import {
  selectHoldingsData,
  selectHoldingsDate,
  selectHoldingsFilter,
  selectHoldingsLoading,
  selectHoldingsMonthFilter,
  selectHoldingsMonths,
  selectHoldingsAllocationType,
  selectHoldingsYearFilter,
  selectHoldingsYears
} from './holdings.selectors';
import {
  selectCountrySelected,
  selectMainFilter,
  selectMainFilterSelectedAccount,
  selectMainFilterSelectedClient,
  selectMainPageNotes,
  selectStaticData
} from '../../shared/layout/main.selectors';
import { distinctUntilChanged, filter, map, take } from 'rxjs/operators';
import {
  HoldingsFileDownloadAction,
  LoadHoldingsAction,
  LoadHoldingsDatesAction,
  ResetHoldingsDataAction,
  SetHoldingsAllocationTypeAction,
  SetHoldingsMonthFilterAction,
  SetHoldingsYearFilterAction
} from './holdings.actions';
import { RGB_COLORS } from '../../global/colors';
import { Month, MONTHS } from '../../global/dates';
import { BehaviorSubject } from 'rxjs';
import { allocationTypeOption, allocationTypeOptions as allAllocationTypeOptions, HoldingsChartData, HoldingsTableItem } from './holdings.models';

import { formatDate } from '../../global/utilities.service';
import { getDataUri } from '../../global/getDataUri';
// 
import * as moment from 'moment';
import { WindowResizeService } from '../../global/window-resize.service';
import { isEqual, sortBy } from 'lodash';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import * as pdfMake from 'pdfmake/build/pdfmake';
import { georgiaFontFamily, georgiaFontVfs } from '../../shared/pdfs/georgia-font';
import { pdfFooter, pdfHeader, pdfStyle } from '../../shared/pdfs/templates';
import { User } from '../administration/user/user.models';
import { selectAuthUser } from '../auth/auth.selectors';
import { selectConsultants } from '../features.selectors';
import { LoadClientConsultantAction } from '../features.actions';
import { CONFIG } from '../../../config';
import { isNullOrUndefined } from 'util';
import { TableColumnAlignment } from 'atlas-table';
import { TableHeaderComponent } from 'atlas-table';
import { TableSortDirections } from 'atlas-table';
import { Actions, FieldSize } from 'atlas-cdk';
import { DropdownPlacement, DropdownTriggerType } from 'atlas-menu';
import { ToastService } from '../../shared/toaster_/toaster_.service';
@Component({
  standalone: false,
  selector: 'dsp-holdings',
  templateUrl: 'holdings.container.html',
  styleUrls: ['holdings.container.scss']
})
export class HoldingsContainerComponent implements OnInit, OnDestroy, AfterViewInit {
  dropdownTriggerTypeIconButton = DropdownTriggerType.IconButton;
  dropdownPlacementLeftStart = DropdownPlacement.LeftStart;

  sizeMedium = FieldSize.medium;

  @ViewChild('donutChart')
  public donutChart: ElementRef;

  @ViewChild('treeChart')
  public treeChart: ElementRef;

  @ViewChild('header') header: TableHeaderComponent;


  public headers: ColumnDef[] = [
    {
      name: 'Name',
      column: 'name',
      sortable: true,
      displayLabel: 'Name',
      fieldKey: 'name'
    },
    {
      name: '',
      column: 'color',
      sortable: false,
      displayLabel: '',
      fieldKey: 'color'
    },
    {
      name: 'Allocation',
      column: 'allocation',
      align: TableColumnAlignment.right,
      sortable: {
        direction: 'desc',  // 'asc' or 'desc'
        sortOnLoad: true    // This will sort the data on initialization
      },
      displayLabel: 'Allocation',
      fieldKey: 'allocation'
    }
  ];
  public year: number;

  public month: number;
  public option: any;
  public allocationType: string = 'managerAllocations';

  public tab = 0;
  public accountId;
  public loading$: Observable<boolean>;
  public months$: Observable<Month[]>;
  public monthFilterOptions$: Observable<{ label: string; value: number }[]>;
  public options$: Observable<[]>;
  public optionFilterOptions$: Observable<{ label: string; value: any }[]>;
  public years$: Observable<number[]>;
  public yearFilterOptions$: Observable<{ label: number; value: number }[]>;
  public date$: Observable<string>;
  public donut$: Observable<Chart>;
  public tree$: Observable<Chart>;
  public table$: Observable<HoldingsTableItem[]>;
  public drilldown$: BehaviorSubject<string> = new BehaviorSubject<string>('');
  public sort$: BehaviorSubject<SortInfo> = new BehaviorSubject<SortInfo>({ column: 'allocation', reverse: true });
  public isMobile$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public notes$: BehaviorSubject<PageNotes> = new BehaviorSubject<PageNotes>({} as PageNotes);
  public allocationTypeOptions: allocationTypeOption[] = allAllocationTypeOptions;

  private readonly redraw$: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  public user$: Observable<User | undefined>;

  private readonly subscription: Subscription = new Subscription();
  public country = '';

  public constructor(
    private readonly store: Store<AppState>,
    //public config: MosConfigurationService,
    private readonly resize: WindowResizeService,
    private readonly _toasterService: ToastService
  ) {
    this.loading$ = this.selectLoading();
    this.years$ = this.selectYears();
    this.yearFilterOptions$ = this.years$.pipe(
      map(years => years.map(year => ({
        label: year,
        value: year
      })))
    );
    this.months$ = this.selectMonths();
    this.monthFilterOptions$ = this.months$.pipe(
      map(months => months.map(month => ({
        label: month.name,
        value: month.value
      })))
    );
    this.options$ = this.selectOptions();
    this.optionFilterOptions$ = this.options$.pipe(
      map(options => options.map((option: { name: string; fundId: any }) => ({
        label: option.name,
        value: option.fundId,
        selected: option.fundId === this.option
      })))
    );
    this.date$ = this.selectDate();
    this.donut$ = this.selectDonutChart();
    this.tree$ = this.selectTreeChart();
    this.table$ = this.selectTable();
    this.user$ = this.store.pipe(select(selectAuthUser));
    this.sort$;
    this.allocationType = this.allocationTypeOptions[0].value;
  }

  ngAfterViewInit(): void {
    this.sort$.subscribe(sortInfo => {
      if (this.header) {
        this.header.sortOption = {
          field: sortInfo.column,
          direction: sortInfo.reverse ? TableSortDirections.descending : TableSortDirections.ascending,
        };
      }
    });
  }

  public ngOnInit(): void {
    this.subscription.add(this.subscribeSelectedCountry());
    this.subscription.add(this.subscribeLoadHoldingsDates());
    this.subscription.add(this.subscribeLoadHoldings());
    this.subscription.add(this.subscribeInitialYearFilter());
    this.subscription.add(this.subscribeYearFilter());
    this.subscription.add(this.subscribeInitialMonthFilter());
    this.subscription.add(this.subscribeMonthFilter());
    this.subscription.add(this.subscribeIsMobile());
    this.subscription.add(this.subscribeNotes());
    this.subscription.add(this.subscribeConsultantLoad());
    this.subscription.add(this.subscribeAllocationType());
  }

  public subscribeSelectedCountry(): Subscription {
    return this.store.pipe(
      select(selectCountrySelected)
    ).subscribe(country => {
      this.country = country;
      this.updateAllocationTypeOptions(this.month);
    });
  }

  private subscribeAllocationType(): Subscription {
    return this.store.pipe(
      select(selectHoldingsAllocationType)
    ).subscribe(allocationType => {
      if (allocationType) {
        this.allocationType = allocationType;
      }
    });
  }

  public ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  public onChangeTab(tab: number): void {
    if (this.drilldown$.value) {
      this.redraw$.next(tab);
      this.drilldown$.next('');
    }
    // Reset selected option to Total Portfolio
    this.option = 'Total Portfolio';
  }

  private subscribeConsultantLoad(): Subscription {
    return this.user$.subscribe(user => {
      if (user) {
        this.store.pipe(select(selectMainFilterSelectedClient)).subscribe(client => {
          if (!isNullOrUndefined(client)) {
            this.store.dispatch(new LoadClientConsultantAction(client._id));
          }
        });
      }
    });
  }


  public onChangeYear(event: number): void {
    if (event !== null && event !== undefined) {
      this.store.dispatch(new SetHoldingsYearFilterAction(event));

      this.store.pipe(
        select(selectHoldingsMonths),
        take(1)
      ).subscribe(months => {
        this.store.dispatch(new SetHoldingsMonthFilterAction(months[months.length - 1].value));
      });
    }
  }


  public onChangeMonth(event: number): void {
    if (event !== null && event !== undefined) {
      this.store.dispatch(new SetHoldingsMonthFilterAction(event));
    }
  }

  public onSort(event: SortInfo): void {
    // this.sort$.next(event);
    const currentSort = this.sort$.value;
    if (currentSort.column === event.column) {
      this.sort$.next({ column: event.column, reverse: !currentSort.reverse });
    } else {
      this.sort$.next({ column: event.column, reverse: false });
    }
  }

  public onExportCSV(): void {
    combineLatest([
      this.store.pipe(select(selectMainFilterSelectedAccount)),
      this.store.pipe(select(selectHoldingsDate)),
      this.drilldown$,
      this.table$,
      this.store.pipe(select(selectHoldingsAllocationType))
    ]).pipe(
      filter(([account, date]) => !!account && !!date),
      map(([account, date, id, table, allocationType]) =>
      ({
        account: account._id || '',
        date: date || '',
        fund: id,
        table,
        allocationType,
        userDate: moment().format('DD/MM/YYYY h:mm:ss a ') + Intl.DateTimeFormat().resolvedOptions().timeZone,
        country: this.country
      }
      )),
      take(1)
    ).subscribe(payload => {
      if (payload.table.length) {
        this.store.dispatch(new HoldingsFileDownloadAction(payload));
      } else {
        //this.store.dispatch(new AddErrorToastAction(`There is no data in specified date.`));
        return this._toasterService.launchAlertError("There is no data in specified date.", Actions.error);
      }
    });
  }

  public onExportPDF(): void {
    // combineLatest(
    //   this.store.pipe(select(selectMainFilter)),
    //   this.table$,
    //   this.store.pipe(select(selectHoldingsFilter)),
    //   this.drilldown$,
    //   this.date$,
    //   this.store.pipe(select(selectStaticData)),
    //   this.store.pipe(select(selectMainPageNotes)),
    //   this.store.pipe(select(selectConsultants))
    // ).pipe(
    combineLatest([
      this.store.pipe(select(selectMainFilter)),
      this.table$,
      this.store.pipe(select(selectHoldingsFilter)),
      this.drilldown$,
      this.date$,
      this.store.pipe(select(selectStaticData)),
      this.store.pipe(select(selectMainPageNotes)),
      this.store.pipe(select(selectConsultants))
    ]).pipe(
      filter(([mainFilter, data, filter, drilldown, date, staticData, pageNotes, consultants]) =>
        !!mainFilter.account[0]
      ),
      map(([mainFilter, data, filter, drilldown, date, staticData, pageNotes, consultants]) =>
      ({
        client: mainFilter.client[0].name,
        accountName: mainFilter.account[0].name,
        accountNumber: mainFilter.account[0].number,
        data,
        year: filter.year,
        month: filter.month,
        drilldown,
        date,
        staticData,
        pageNotes,
        consultants,
        country: this.country
      }
      )),
      take(1)
    ).subscribe(payload => {
      if (payload.data.length > 0) {
        const body = payload.data.map((data, index) => {
          let color = null;
          if (data.color) {
            color = data.color.replace(/^(rgb|rgba)\(/, '').replace(/\)$/, '').replace(/\s/g, '').split(',');
          }

          if (this.tab === 1 && payload.drilldown) {
            return [
              { text: data.name || '-', bold: index === 0, style: 'textCell' },
              {
                text: data.allocation ? data.allocation + '%' : '-',
                bold: index === 0,
                style: 'numberCell'
              }
            ];
          } else {
            return [
              { text: data.name || '-', bold: index === 0, style: 'textCell' },
              {
                canvas: color ? [
                  {
                    type: 'ellipse',
                    x: 7, y: 7,
                    color: [color[0], color[1], color[2]],
                    fillOpacity: 1,
                    r1: 7, r2: 7
                  }
                ] : [],
                style: 'textCell'
              },
              {
                text: data.allocation ? data.allocation + '%' : '-',
                bold: index === 0,
                style: 'numberCell'
              }
            ];
          }
        }),

          mmcLogo = [],
          mercerLogo = [],
          icons = [],

          promises = [];
        promises.push(getDataUri('assets/images/mmc_logo.png', CommonConstants.pdfMmcLogoWidth, CommonConstants.pdfMmcLogoHeight)
          .then(data => mmcLogo.push(data)));
        promises.push(getDataUri('assets/images/mercer_brand_logo.png',
          CommonConstants.pdfMercerLogoWidth, CommonConstants.pdfMercerLogoHeight)
          .then(data => mercerLogo.push(data)));
        promises.push(getDataUri('assets/images/phone.png', 18, 18)
          .then(data => icons['phone'] = data));
        promises.push(getDataUri('assets/images/email.png', 18, 18)
          .then(data => icons['email'] = data));
        promises.push(getDataUri('assets/images/website.png', 18, 18)
          .then(data => icons['website'] = data));
        setTimeout(() => {
          Promise.all(promises).then(() => {
            const date = `${MONTHS[payload.month].name} ${payload.year}`.toUpperCase(),
              address = [];
            address.push(
              {
                text: !isNullOrUndefined(payload.staticData.entityName) ?
                  `${payload.staticData.entityName}` : '', style: 'clientTitle', pageBreak: 'before'
              },
              {
                text: !isNullOrUndefined(payload.staticData.address) ?
                  `${payload.staticData.address}` : '', style: 'clientAddress'
              },
            );
            const docDefinition = {
              content: [
                { text: 'Holdings Report', style: 'title' },
                { text: date, style: 'subtitle' },
                { text: `${payload.client} - ${payload.accountName} - ${payload.accountNumber}`, style: 'subsubtitle' },
                { text: 'Date Generated: ' + moment().format(CommonConstants.displayDateFormatInAU), style: 'subtitleDate' },
                { text: 'Funds Allocation', style: 'chartTitleNew' },
                { text: `as at end of ${payload.date}`, style: 'subChartTitleNew' },
                {
                  table: this.tab === 1 && payload.drilldown ? {
                    headerRows: 1,
                    widths: ['*', 'auto'],
                    body: [
                      [
                        { text: 'NAME', style: 'tableHeader' },
                        { text: 'ALLOCATION', style: 'tableHeader' },
                      ],
                      ...body
                    ]
                  } : {
                    headerRows: 1,
                    widths: ['*', 'auto', 'auto'],
                    body: [
                      [
                        { text: 'NAME', style: 'tableHeader' },
                        { text: '', style: 'tableHeader' },
                        { text: 'ALLOCATION', style: 'tableHeader' },
                      ],
                      ...body
                    ]
                  },
                  layout: {
                    hLineWidth: function (i, node) {
                      return 1;
                    },
                    vLineWidth: function (i, node) {
                      return 1;
                    },
                    hLineColor: function (i, node) {
                      return 'white';

                    },
                    vLineColor: function (i, node) {
                      return 'white';
                    },
                  }
                },
                { text: CommonConstants.notes, style: 'notesTitle', pageBreak: 'before' },
                { text: `${payload.pageNotes.holdings.replace(/<br\/>/g, '\n')}`, style: 'notesData', pageBreak: 'after' },
                ...payload.consultants.map(consultant => (
                  [
                    { text: `${consultant.firstName} ${consultant.lastName}` || '', style: 'consultantName' },
                    {
                      columns: [
                        // { image: icons['phone'], width: 15, height: 11, margin: [0, 0, 0, 10] },
                        { text: `${consultant.phone}` || '', style: 'consultantPhone' }
                      ]
                    },
                    {
                      columns: [
                        // { image: icons['email'], width: 15, height: 11, margin: [0, 0, 0, 10] },
                        { text: `${consultant.username}` || '', style: 'consultantMail' },
                      ]
                    },
                  ]
                )),
                { text: 'Mercer Multi Manager Fund Contact Details', style: 'fundContactTitle' },
                {
                  columns: [
                    // { image: icons['phone'], width: 15, height: 11, margin: [0, 0, 0, 10] },
                    { text: `${payload.staticData.phone || ''}`, style: 'fundContactPhone' }
                  ]
                },
                {
                  columns: [
                    // { image: icons['email'], width: 15, height: 11, margin: [0, 0, 0, 10] },
                    { text: `${payload.staticData.email || ''}`, style: 'fundContactEmail' }
                  ]
                },
                {
                  columns: [
                    // { image: icons['website'], width: 15, height: 11, margin: [0, 0, 0, 10] },
                    { text: `${payload.staticData.website || ''}`, style: 'fundContactWebsite' }
                  ]
                },
                { text: CommonConstants.importantNotices, style: 'noticeTitle', pageBreak: 'before' },
                { text: payload.staticData.importantNotices, style: 'noticeData' },
                ...address
              ],
              styles: {
                ...pdfStyle,
              },
              footer(currentPage: number, totalPages: number) {
                return pdfFooter(currentPage, totalPages, payload, mmcLogo[0]);
              },
              header(currentPage: number, totalPages: number) {
                return pdfHeader(mmcLogo, 'Holdings Report', currentPage, totalPages);
              },
              pageMargins: [40, 90, 40, 100]
            };

            pdfMake.vfs = {
              ...(pdfFonts.pdfMake as any).vfs,
              ...georgiaFontVfs
            };
            pdfMake.fonts = {
              ...georgiaFontFamily,
              Roboto: {
                normal: 'Roboto-Regular.ttf',
                bold: 'Roboto-Medium.ttf',
                italics: 'Roboto-Italic.ttf',
                bolditalics: 'Roboto-MediumItalic.ttf'
              }
            };
            pdfMake.createPdf(docDefinition).download('HoldingsExport.pdf');
          });
        }, 1000);

      } else {
        // this.store.dispatch(new AddErrorToastAction(`There is no data in specified ${this.year} and ${this.month}.`));
        return this._toasterService.launchAlertError(`There is no data in specified ${this.year} and ${this.month}.`, Actions.error);
      }
    });
  }

  private subscribeInitialYearFilter(): Subscription {
    // return combineLatest(
    //   this.store.pipe(select(selectHoldingsYears)),
    //   this.store.pipe(select(selectHoldingsYearFilter))
    // ).pipe(
    return combineLatest([
      this.store.pipe(select(selectHoldingsYears)),
      this.store.pipe(select(selectHoldingsYearFilter))
    ]).pipe(
      filter(([years]) => !!years.length),
      map(([years, year]) => year !== undefined ? year : years[0])
    ).subscribe(year => {
      this.store.dispatch(new SetHoldingsYearFilterAction(year));
    });
  }

  private subscribeYearFilter(): Subscription {
    return this.store.pipe(
      select(selectHoldingsYearFilter)
    ).subscribe(year => {
      this.year = year !== undefined ? year : undefined;

      this.drilldown$.next('');
    });
  }

  private subscribeInitialMonthFilter(): Subscription {
    // return combineLatest(
    //   this.store.pipe(select(selectHoldingsMonths)),
    //   this.store.pipe(select(selectHoldingsMonthFilter))
    // ).pipe(
    return combineLatest([
      this.store.pipe(select(selectHoldingsMonths)),
      this.store.pipe(select(selectHoldingsMonthFilter))
    ]).pipe(
      filter(([months, month]) => !!months.length && month === undefined),
      map(([months, month]) => month !== undefined ? month : months[months.length - 1].value)
    ).subscribe(month => {
      this.store.dispatch(new SetHoldingsMonthFilterAction(month));
    });
  }

  private subscribeMonthFilter(): Subscription {
    return this.store.pipe(
      select(selectHoldingsMonthFilter)
    ).subscribe(month => {
      this.month = month !== undefined ? month : undefined;
      this.updateAllocationTypeOptions(this.month);

      this.drilldown$.next('');
    });
  }

  /**
   * It enables sector allocation and region allocation options only for AU quarter-end months.
   */
  private updateAllocationTypeOptions(month?: number): void {
    const isQuarterEndMonth = [2, 5, 8, 11].includes(month);
    const isAuCountry = this.country === 'au';
    const canShowAllAllocationTypes = isQuarterEndMonth && isAuCountry;

    this.allocationTypeOptions = canShowAllAllocationTypes
      ? allAllocationTypeOptions
      : allAllocationTypeOptions.filter(
        option => option.value === 'managerAllocations'
      );

    if (!canShowAllAllocationTypes && this.allocationType !== 'managerAllocations') {
      this.allocationType = 'managerAllocations';
      this.store.dispatch(new SetHoldingsAllocationTypeAction(this.allocationType));
    }
  }

  private subscribeLoadHoldingsDates(): Subscription {
    return this.store.pipe(
      select(selectMainFilterSelectedAccount),
      distinctUntilChanged(isEqual)
    ).subscribe(account => {
      if (!!account) {
        this.accountId = account._id;
        this.store.dispatch(new LoadHoldingsDatesAction({ account: account._id }));
      } else {
        this.store.dispatch(new ResetHoldingsDataAction());
      }
    });
  }

  private subscribeLoadHoldings(): Subscription {
    return combineLatest([
      this.store.pipe(select(selectHoldingsDate)),
      this.store.pipe(select(selectHoldingsAllocationType))
    ]).pipe(
      distinctUntilChanged(isEqual)
    ).subscribe(([date, allocationType]) => {
      if (!!this.accountId && !!date) {
        this.store.dispatch(new LoadHoldingsAction({ account: this.accountId || '', date, country: this.country, allocationType }));
      } else {
        this.store.dispatch(new ResetHoldingsDataAction());
      }
    });
  }

  private subscribeIsMobile(): Subscription {
    return this.resize.mobile$.subscribe(this.isMobile$);
  }

  private subscribeNotes(): Subscription {
    return this.store.pipe(
      select(selectMainPageNotes)
    ).subscribe(this.notes$);
  }

  private selectLoading(): Observable<boolean> {
    return this.store.pipe(
      select(selectHoldingsLoading)
    );
  }

  private selectDate(): Observable<string> {
    return this.store.pipe(
      select(selectHoldingsDate),
      map(date => formatDate(date || '', 'MMM YYYY'))
    );
  }

  private selectYears(): Observable<number[]> {
    return this.store.pipe(
      select(selectHoldingsYears)
    );
  }

  private selectOptions(): Observable<any> {

    return this.store.pipe(select(selectHoldingsData)
    ).pipe(
      map((holdings) => {
        const rec = holdings.map(holding => {
          return {
            name: holding.name,
            fundId: holding.fundId
          };
        });

        rec.unshift({
          name: 'Total Portfolio',
          fundId: 'Total Portfolio'
        });
        if (!this.drilldown$.value) {
          this.option = rec[0].fundId;
        }
        return rec;
      })
    );
  }

  public onChangeOption(event: any): void {
    // If "Total Portfolio" is selected, reset drilldown to show all holdings
    if (event === 'Total Portfolio') {
      this.drilldown$.next('');
      this.allocationType = this.allocationTypeOptions[0].value;
      this.store.dispatch(new SetHoldingsAllocationTypeAction(this.allocationType));
    } else {
      this.store.pipe(
        select(selectHoldingsData),
        take(1)
      ).subscribe(holdings => {
        const selectedFund = holdings.find(holding => holding.fundId === event);

        this.drilldown$.next(event);
        // Always open Level 2 to Manager Allocations
        this.allocationType = this.allocationTypeOptions[0].value;
        this.store.dispatch(new SetHoldingsAllocationTypeAction(this.allocationType));
      });
    }
  }

  public onChangeAllocationType(event: string): void {
    this.allocationType = event;
    this.store.dispatch(new SetHoldingsAllocationTypeAction(event));
  }

  public get isAtLevel2(): boolean {
    return !!this.drilldown$.value && this.drilldown$.value !== '' && this.option !== 'Total Portfolio';
  }

  private selectMonths(): Observable<Month[]> {
    return this.store.pipe(
      select(selectHoldingsMonths)
    );
  }

  private selectDonutChart(): Observable<Chart> {
    return combineLatest([
      this.store.pipe(
        select(selectHoldingsData),
        distinctUntilChanged(isEqual)
      ),
      this.redraw$,
      this.drilldown$
    ]).pipe(
      map(([data, redraw, drilldown]) => ({ data, drilldown })),
      map(({ data, drilldown }) => {
        const container = this,
          drilldownData = [];

        /** Color Index, resets if drilldown items exceed available colors **/
        let parentColorIndex = 0;

        const chartData = data.map(holding => {
          const drilldownSeries = {
            name: holding.name,
            id: holding.fundId,
            innerSize: '65%',
            size: '100%',
            data: []
          };

          let childColorIndex = 0;

          holding.funds.forEach(fund => {
            if (childColorIndex >= RGB_COLORS.length) {
              childColorIndex = 0;
            }
            ++childColorIndex;
            const color = RGB_COLORS[RGB_COLORS.length - childColorIndex];
            drilldownSeries.data.push({
              name: fund.name,
              y: parseFloat(fund.allocation || '0.0000'),
              color: `rgba(${color.r}, ${color.g}, ${color.b}, 1)`
            });
          });

          drilldownData.push(drilldownSeries);

          if (parentColorIndex >= RGB_COLORS.length) {
            parentColorIndex = 0;
          }
          parentColorIndex++;
          const color = RGB_COLORS[parentColorIndex];

          return {
            name: holding.name,
            y: parseFloat(holding.allocation || '0.0000'),
            drilldown: holding.fundId,
            color: `rgba(${color.r}, ${color.g}, ${color.b}, 1)`
          };
        });

        return new Chart({
          chart: {
            type: 'pie',
            style: {
              fontFamily: CommonConstants.semanticFontFamilyPlain,
            },
            events: {
              load() {
                // Drill down to selected fund on initial load if drilldown is active
                if (drilldown) {
                  const series = this.series[0],
                    point = series.points.find((p: any) => p.drilldown === drilldown);
                  if (point) {
                    // @ts-ignore - Highcharts specific method
                    point.doDrilldown();
                  }
                }
              },
              drilldown: event => {
                // Prevent recursive updates by checking if value is different
                if (container.drilldown$.value !== event.seriesOptions.id) {
                  container.drilldown$.next(event.seriesOptions.id || '');
                  // Update the dropdown to show the selected fund
                  container.option = event.seriesOptions.id || '';
                  // Always open Level 2 to Manager Allocations
                  container.allocationType = container.allocationTypeOptions[0].value;
                  container.store.dispatch(new SetHoldingsAllocationTypeAction(container.allocationType));
                }
              },
              drillup: () => {
                // Prevent recursive updates by checking if value is already empty
                if (container.drilldown$.value !== '') {
                  container.drilldown$.next('');
                  // Reset the dropdown to "Total Portfolio"
                  container.option = 'Total Portfolio';
                }
              }
            },
          },
          credits: {
            enabled: false
          },
          title: {
            style: {
              display: 'none'
            }
          },
          plotOptions: {
            pie: {
              shadow: false,
              center: ['50%', '50%'],
              dataLabels: {
                enabled: false
              },
            }
          },
          tooltip: {
            valueSuffix: '%',
            style: {
              fontFamily: CommonConstants.semanticFontFamilyPlain
            }
          },
          series: [
            {
              type: 'pie',
              name: 'Total Mercer Portfolio',
              innerSize: '65%',
              size: '100%',
              data: chartData
            }
          ],
          drilldown: {
            series: drilldownData
          },
          exporting: {
            buttons: {
              contextButton: {
                enabled: false
              }
            }
          }
        });
      })
    );
  }

  private selectTreeChart(): Observable<Chart> {
    // return combineLatest(
    //   this.store.pipe(select(selectHoldingsData), distinctUntilChanged(isEqual)),
    //   this.redraw$
    // ).pipe(
    return combineLatest([
      this.store.pipe(select(selectHoldingsData), distinctUntilChanged(isEqual)),
      this.redraw$,
      this.drilldown$]
    ).pipe(
      map(([data, redraw, drilldown]) => ({ data, drilldown })),
      map(({ data, drilldown }) => {
        const container = this,
          points: HoldingsChartData[] = [];
        let redrawingInProcess = false,

          /** Color Index, resets if drilldown items exceed available colors **/
          colorIndex = 0;

        data.forEach(holding => {
          if (colorIndex >= RGB_COLORS.length) {
            colorIndex = 0;
          }
          colorIndex++;
          const color = RGB_COLORS[colorIndex];

          points.push({
            id: holding.fundId,
            name: holding.name,
            value: parseFloat(holding.allocation || '0.0000'),
            color: `rgba(${color.r}, ${color.g}, ${color.b}, 1)`,
          });

          holding.funds.forEach(fund => {
            points.push({
              parent: holding.fundId,
              name: fund.name,
              value: parseFloat(fund.allocation || '0.0000'),
            });
          });
        });

        function recolorZones(chart, rootId?) {
          const series = chart.series[0],
            targetRootId = rootId || (series as any).rootNode;

          if (!targetRootId) {
            redrawingInProcess = false;
            return;
          }

          // Find the parent point and its children
          const parentPoint = series.points.find(point => point.id === targetRootId),
            points = series.points.filter(point => point.parent === targetRootId);

          let childColorIndex = 0;
          points.forEach(point => {
            if (childColorIndex >= RGB_COLORS.length) {
              childColorIndex = 0;
            }
            ++childColorIndex;
            const color = RGB_COLORS[RGB_COLORS.length - childColorIndex];
            point.update({ color: `rgba(${color.r}, ${color.g}, ${color.b}, 1)` }, false);
          });

          chart.redraw();
          redrawingInProcess = false;
        }

        return new Chart({
          chart: {
            style: {
              fontFamily: CommonConstants.semanticFontFamilyPlain,
            },
            events: {
              load() {
                // Drill down to selected fund on initial load if drilldown is active
                if (drilldown) {
                  const series = this.series[0];
                  // @ts-ignore - Highcharts treemap specific method
                  series.drillToNode(drilldown);

                  // Recolor the drilled-down zones
                  setTimeout(() => {
                    recolorZones(this, drilldown);
                  }, 10);
                }
              },
              redraw() {
                if (!document.getElementsByClassName('highcharts-drillup-button')[0] && !redrawingInProcess) {
                  if (container.drilldown$.value !== '') {
                    container.drilldown$.next('');
                    // Reset the dropdown to "Total Portfolio"
                    container.option = 'Total Portfolio';
                  }
                  redrawingInProcess = true;
                  recolorZones(this, (this.series[0] as any).rootNode);
                }
              },
            }
          },
          credits: {
            enabled: false
          },
          series: [
            {
              type: 'treemap',
              events: {
                click: (event: any) => {
                  if (event.point.id.indexOf('highcharts') !== -1) {
                    event.preventDefault();
                    return;
                  }
                  if (event.point.id) {
                    if (container.drilldown$.value !== event.point.id) {
                      container.drilldown$.next(event.point.id);
                      // Update the dropdown to show the selected fund
                      container.option = event.point.id;
                      // Always open Level 2 to Manager Allocations
                      container.allocationType = container.allocationTypeOptions[0].value;
                      container.store.dispatch(new SetHoldingsAllocationTypeAction(container.allocationType));

                      // Trigger recoloring after drilldown
                      setTimeout(() => {
                        const chart = event.point.series.chart;
                        recolorZones(chart, event.point.id);
                      }, 10);
                    }
                  }
                }
              },
              layoutAlgorithm: 'squarified',
              allowTraversingTree: true,
              // allowDrillToNode: true,
              dataLabels: {
                enabled: false
              },
              levelIsConstant: false,
              levels: [
                {
                  level: 1,
                  dataLabels: {
                    enabled: true,
                  },
                  borderWidth: 3
                }
              ],
              data: points,
            },
          ],
          plotOptions: {
            series: {
              point: {
                events: {
                  click() {
                    // Use the recolorZones function for consistency
                    if ((this as any).id) {
                      setTimeout(() => {
                        recolorZones((this as any).series.chart, (this as any).id);
                      }, 10);
                    }
                  },
                },
              },
            },
          },
          exporting: {
            buttons: {
              contextButton: {
                enabled: false
              }
            }
          },
          tooltip: {
            valueSuffix: '%',
            style: {
              fontFamily: CommonConstants.semanticFontFamilyPlain
            }
          },
          subtitle: {
            style: {
              display: 'none'
            }
          },
          title: {
            style: {
              display: 'none'
            }
          }
        });
      })
    );
  }

  private selectTable(): Observable<HoldingsTableItem[]> {
    // return combineLatest(
    //   this.store.pipe(select(selectHoldingsData)),
    //   this.drilldown$,
    //   this.sort$,
    //   this.redraw$
    // ).pipe(
    return combineLatest([
      this.store.pipe(select(selectHoldingsData)),
      this.drilldown$,
      this.sort$,
      this.redraw$]
    ).pipe(
      map(([holdings, drilldown, sort]) => {
        if (!drilldown) {
          let colorIndex = 0;

          const mapped = holdings.map(holding => {
            if (colorIndex >= RGB_COLORS.length) {
              colorIndex = 0;
            }
            colorIndex++;
            const color = RGB_COLORS[colorIndex];
            return {
              name: holding.name,
              color: `rgba(${color.r}, ${color.g}, ${color.b}, 1)`,
              allocation: holding.allocation
            };
          }),

            sorted = this.sortFunction(mapped, sort);

          sorted.unshift({
            name: 'Total Mercer Portfolio',
            color: '',
            allocation: '100'
          });

          return sorted;
        }

        const selected = holdings.find(holding => holding.fundId === drilldown);
        if (!selected) {
          return [];
        }

        let colorIndex = 0;

        const mapped = selected.funds.map(fund => {
          if (colorIndex >= RGB_COLORS.length) {
            colorIndex = 0;
          }
          ++colorIndex;
          const color = RGB_COLORS[RGB_COLORS.length - colorIndex];

          return {
            name: fund.name,
            color: `rgba(${color.r}, ${color.g}, ${color.b}, 1)`,
            allocation: fund.allocation
          };
        }),

          sorted = this.sortFunction(mapped, sort);

        sorted.unshift({
          name: selected.name,
          color: '',
          allocation: selected.allocation
        });

        return sorted;
      })
    );
  }

  private sortFunction(data: HoldingsTableItem[], sort: SortInfo): HoldingsTableItem[] {
    const sorted = sortBy(data, (holding: { [key: string]: string }) =>
      sort.column === 'allocation' ? parseFloat(holding.allocation) : holding[sort.column]) as HoldingsTableItem[],
      ordered = sort.reverse ? sorted.reverse() : sorted;

    if (this.allocationType === 'regionAllocations') {
      const other = ordered.filter(item => item.name?.toLowerCase() === 'other'),
        rest = ordered.filter(item => item.name?.toLowerCase() !== 'other');
      return [...rest, ...other];
    }

    return ordered;
  }
}
